import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { LEADERSHIP_PAGE } from "../data/leadership-data";

// ── Derived constants from data ────────────────────────────────────────────
const DATA = LEADERSHIP_PAGE;
const sandeepforbes = `${process.env.PUBLIC_URL}/${DATA.imagePaths.sandeepForbes}`;
const sandeepCMU = `${process.env.PUBLIC_URL}/${DATA.imagePaths.sandeepCMU}`;

const PRESS_IMAGES = DATA.press.images.map(
    (path) => `${process.env.PUBLIC_URL}/${path}`
);
const PRESS = DATA.press.outlets;

// ── Press Image Carousel Component ────────────────────────────────────────
const PressImageCarousel = ({ images, onIndexChange }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prev) => {
                const next = (prev + 1) % images.length;
                if (onIndexChange) onIndexChange(next);
                return next;
            });
        }, 4000);
        return () => clearInterval(interval);
    }, [images.length, onIndexChange]);

    useEffect(() => {
        if (onIndexChange) onIndexChange(0);
    }, [onIndexChange]);

    const goTo = (i) => {
        setCurrentIndex(i);
        if (onIndexChange) onIndexChange(i);
    };

    return (
        <div className="relative h-[520px] w-full max-w-sm mx-auto">
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -20 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl bg-[#FFDAB9]"
                >
                    <img
                        src={images[currentIndex]}
                        alt="Press feature"
                        className="w-full h-full object-contain p-4"
                    />
                </motion.div>
            </AnimatePresence>
            <div className="absolute -bottom-8 left-0 right-0 flex justify-center gap-2 z-10">
                {images.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => goTo(i)}
                        className={`h-2 rounded-full transition-all ${
                            i === currentIndex
                                ? "w-8 bg-blue-600"
                                : "w-2 bg-gray-300"
                        }`}
                        aria-label={`Go to image ${i + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

// ── Image Carousel Component ───────────────────────────────────────────────
const ImageCarousel = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const images = DATA.carouselImages.map((img) => ({
        ...img,
        src: `${process.env.PUBLIC_URL}/${img.src}`,
    }));

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [images.length]);

    return (
        <div className="rounded-3xl overflow-hidden shadow-xl relative aspect-[3/4]">
            <AnimatePresence mode="wait">
                <motion.img
                    key={currentIndex}
                    src={images[currentIndex].src}
                    alt={images[currentIndex].alt}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.7, ease: "easeInOut" }}
                    className="w-full h-full object-cover absolute inset-0"
                />
            </AnimatePresence>
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                {images.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            idx === currentIndex
                                ? "bg-white w-6"
                                : "bg-white/50"
                        }`}
                        aria-label={`Go to image ${idx + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

// ── Fade-in helper ──────────────────────────────────────────────────────────
const FadeUp = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
        className={className}
    >
        {children}
    </motion.div>
);

// ── Utility: resolve image path ────────────────────────────────────────────
const img = (relativePath) => `${process.env.PUBLIC_URL}/${relativePath}`;

export default function AboutPage() {
    const [asSeenIndex, setAsSeenIndex] = useState(0);

    return (
        <main className="overflow-x-hidden" data-testid="about-page">
            {/* ── HERO ─────────────────────────────────────────────────────── */}
            <section className="relative min-h-[88vh] bg-white overflow-hidden">
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                    }}
                />
                <div className="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-10 flex flex-col items-center min-h-[88vh]">
                    <div className="w-full max-w-4xl pt-28 pb-16 md:pb-20 text-center">
                        <FadeUp>
                            <p className="text-xs uppercase tracking-[0.35em] text-aiorange font-bold mb-6">
                                {DATA.hero.tag}
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.08}>
                            <h1 className="font-display font-black text-gray-900 text-4xl sm:text-5xl lg:text-6xl leading-[0.97] tracking-tighter max-w-3xl mx-auto">
                                {DATA.hero.headline1}
                                <br />
                                {DATA.hero.headline2}
                                <br />
                                <span className="text-aiorange">
                                    {DATA.hero.headlineHighlight}
                                </span>
                            </h1>
                        </FadeUp>
                        <FadeUp delay={0.16}>
                            {DATA.hero.paragraphs.map((p, i) => (
                                <p
                                    key={i}
                                    className="mt-8 text-gray-600 text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto font-semibold"
                                >
                                    {p.replace(/--/g, "—")}
                                </p>
                            ))}
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* ── VISION / MISSION ─────────────────────────────────────────── */}
            <section className="py-20 bg-white">
                <div className="max-w-5xl mx-auto px-6 sm:px-10 grid md:grid-cols-2 gap-6">
                    <FadeUp>
                        <div
                            className={`${DATA.visionMission.vision.bgClass} rounded-3xl p-8 ${DATA.visionMission.vision.borderClass} h-full`}
                        >
                            <p className="text-xs uppercase tracking-widest font-bold text-aiorange mb-4">
                                {DATA.visionMission.vision.tag}
                            </p>
                            <h3 className="font-display font-bold text-2xl leading-snug text-gray-900">
                                {DATA.visionMission.vision.text}
                            </h3>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <div
                            className={`${DATA.visionMission.mission.bgClass} rounded-3xl p-8 h-full`}
                        >
                            <p className="text-xs uppercase tracking-widest font-bold text-aiorange mb-4">
                                {DATA.visionMission.mission.tag}
                            </p>
                            <h3 className="font-display font-bold text-2xl leading-snug">
                                {DATA.visionMission.mission.text}
                            </h3>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* ── FOUNDER INTRO ────────────────────────────────────────────── */}
            <section className="py-24 bg-[#0C0F1A]" id="founder">
                <div className="max-w-5xl mx-auto px-6 sm:px-10">
                    <FadeUp>
                        <p className="text-xs uppercase tracking-[0.35em] text-white font-bold mb-4">
                            {DATA.founder.tag}
                        </p>
                    </FadeUp>
                    <div className="grid lg:grid-cols-[1fr_1.6fr] gap-14 items-start">
                        <FadeUp delay={0.05}>
                            <div className="relative">
                                <div className="rounded-3xl overflow-hidden aspect-[3/4] bg-aiblue/20">
                                    <img
                                        src={sandeepforbes}
                                        alt="Sandeep Konam — Founder, AI Prof"
                                        className="w-full h-full object-cover object-top"
                                    />
                                </div>
                                <div className="absolute -bottom-4 -right-4 bg-aiorange text-white rounded-2xl px-4 py-3 shadow-xl">
                                    <p className="font-display font-black text-sm leading-tight">
                                        {DATA.founder.forbesBadge.label}
                                    </p>
                                    <p className="text-xs opacity-90 font-semibold">
                                        {DATA.founder.forbesBadge.sublabel}
                                    </p>
                                </div>
                            </div>
                        </FadeUp>
                        <div>
                            <FadeUp delay={0.1}>
                                <h2 className="font-display font-black text-white text-4xl sm:text-5xl tracking-tight">
                                    {DATA.founder.name}
                                </h2>
                                <p className="text-aiorange font-semibold mt-2 text-lg">
                                    {DATA.founder.title}
                                </p>
                            </FadeUp>
                            <FadeUp delay={0.14}>
                                {DATA.founder.bioParagraphs.map((p, i) => (
                                    <p
                                        key={i}
                                        className={`${
                                            i === 0 ? "mt-6" : "mt-4"
                                        } text-white text-lg leading-relaxed`}
                                    >
                                        {p}
                                    </p>
                                ))}
                            </FadeUp>
                            <FadeUp delay={0.22}>
                                <blockquote className="mt-8">
                                    <p className="text-white text-xl font-medium italic leading-snug">
                                        "{DATA.founder.quote.text}"
                                    </p>
                                    <cite className="mt-3 block text-white text-sm not-italic">
                                        {DATA.founder.quote.cite}
                                    </cite>
                                </blockquote>
                            </FadeUp>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── JOURNEY GALLERY ──────────────────────────────────────────── */}
            <section className="py-24 bg-white">
                <div className="max-w-6xl mx-auto px-6 sm:px-10">
                    <FadeUp>
                        <p className="text-xs uppercase tracking-[0.35em] text-aiorange font-bold mb-2">
                            {DATA.journeyGallery.tag}
                        </p>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-normal text-gray-900 mb-4">
                            {DATA.journeyGallery.headline}
                        </h2>
                        <p className="text-gray-500 text-lg max-w-xl mb-14">
                            {DATA.journeyGallery.description}
                        </p>
                    </FadeUp>
                    <div
                        className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5"
                        style={{ gridAutoRows: "360px" }}
                    >
                        {DATA.journeyGallery.images.map((item, idx) => (
                            <FadeUp
                                key={idx}
                                delay={item.delay}
                                className={`${item.colSpan} relative group`}
                            >
                                <div className="relative w-full h-full min-h-0 overflow-hidden rounded-2xl shadow-lg bg-gray-100">
                                    <img
                                        src={img(item.src)}
                                        alt={item.alt}
                                        className={`w-full h-full ${item.objectFit === "contain" ? "object-contain p-3" : "object-cover"} object-center transition-transform duration-700 group-hover:scale-105`}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                                    <p className="absolute bottom-4 left-4 text-white text-sm font-medium drop-shadow-lg">
                                        {item.caption}
                                    </p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CMU GRADUATION PHOTO ─────────────────────────────────────── */}
            <section className="py-0 bg-[#0C0F1A]">
                <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20 grid md:grid-cols-2 gap-10 items-center">
                    <FadeUp>
                        <div className="rounded-3xl overflow-hidden shadow-2xl">
                            <img
                                src={sandeepCMU}
                                alt="Sandeep receiving his MS degree at Carnegie Mellon University"
                                className="w-full h-auto object-cover"
                            />
                        </div>
                        <p className="mt-3 text-white/30 text-xs text-center">
                            {DATA.cmuSection.caption}
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <p className="text-xs uppercase tracking-widest font-bold text-aiorange mb-4">
                            {DATA.cmuSection.tag}
                        </p>
                        <h3 className="font-display font-black text-white text-3xl sm:text-4xl leading-tight mb-5">
                            {DATA.cmuSection.headline}
                        </h3>
                        <p className="text-white/65 text-lg leading-relaxed">
                            {DATA.cmuSection.description}
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* ── COMMUNITY PHOTO ──────────────────────────────────────────── */}
            <section className="py-20 bg-[#F9F7F4]">
                <div className="max-w-5xl mx-auto px-6 sm:px-10 grid md:grid-cols-[1.6fr_1fr] gap-10 items-center">
                    <FadeUp>
                        <p className="text-xs uppercase tracking-widest font-bold text-aiorange mb-4">
                            {DATA.community.tag}
                        </p>
                        <h3 className="font-display font-black text-gray-900 text-3xl sm:text-4xl leading-tight mb-5">
                            {DATA.community.headline}
                        </h3>
                        <p className="text-gray-600 text-lg leading-relaxed">
                            {DATA.community.description}
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <ImageCarousel />
                    </FadeUp>
                </div>
            </section>

            {/* ── AS SEEN IN ───────────────────────────────────────────────── */}
            <section className="py-16 bg-white border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-6 sm:px-10">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <PressImageCarousel
                            images={PRESS_IMAGES}
                            onIndexChange={setAsSeenIndex}
                        />
                        <div>
                            <FadeUp>
                                <p className="text-xs uppercase tracking-[0.35em] text-gray-400 font-bold mb-6">
                                    As seen in
                                </p>
                            </FadeUp>
                            <FadeUp delay={0.05}>
                                <div className="space-y-8">
                                    {PRESS.map((p, i) => (
                                        <div key={p.name} className="group">
                                            <p
                                                className={`font-display font-black text-3xl transition-colors ${
                                                    i === asSeenIndex
                                                        ? "text-aiorange"
                                                        : "text-gray-900 group-hover:text-aiorange"
                                                }`}
                                            >
                                                {p.name}
                                            </p>
                                            <p className="text-sm text-gray-500 mt-1 font-medium tracking-wide">
                                                {p.label}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </FadeUp>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA ──────────────────────────────────────────────────────── */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full blur-[120px] opacity-15 bg-aiblue pointer-events-none" />
                <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full blur-[120px] opacity-10 bg-aiorange pointer-events-none" />
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                    }}
                />
                <div className="relative max-w-3xl mx-auto px-6 sm:px-10 text-center">
                    <FadeUp>
                        <p className="text-aiorange text-xs uppercase tracking-[0.35em] font-bold mb-4">
                            {DATA.cta.tag}
                        </p>
                        <h2 className="font-display font-black text-gray-900 text-4xl sm:text-5xl tracking-normal leading-tight">
                            {DATA.cta.headline}
                        </h2>
                        <p className="mt-5 text-gray-600 text-lg">
                            {DATA.cta.description}
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <Link to="/waitlist?role=student">
                            <Button
                                className="mt-8 bg-aiorange hover:bg-aiorange/90 text-white rounded-full px-8 py-6 font-semibold text-base shadow-xl shadow-aiorange/30"
                                data-testid="about-cta"
                            >
                                {DATA.cta.buttonText}
                            </Button>
                        </Link>
                    </FadeUp>
                </div>
            </section>
        </main>
    );
}
